import { useCallback } from 'react'

import { useCatalog } from '../../context/CatalogContext.jsx'

import { useI18n }    from '../../../../shared/i18n/useI18n.js'

import { CatalogAdditionalPanel } from './CatalogAdditionalPanel.jsx'
import { CatalogMainBar }         from './CatalogMainBar.jsx'

import { ChevronDown }  from '../../../../shared/ui/icons/index.js'
import { Tooltip }      from '../../../../shared/ui/Tooltip/Tooltip.jsx'

import styles from './CatalogFilters.module.scss'

/**
 * Self-contained catalog filter wrapper for desktop/tablet.
 *
 * Combines:
 *  - CatalogMainBar   — row 1 (title + RangeSlider slots + CTA) + row 2 (non-slider main filters)
 *  - CatalogAdditionalPanel — expandable under-bar panel with remaining filter cards
 *
 * Owns the `isExpanded` state (no prop-drilling needed from parent).
 * Provides minimal 2.5em horizontal padding and a drop-shadow surface.
 *
 * The circular expand button is positioned on the bottom edge, centered,
 * overlapping the panel boundary so it visually "opens" the drawer.
 */
export function CatalogFilters() {
  const { t } = useI18n()
  const {
    activeFiltersConfig,
    activeFiltersCount,
    catalogCapabilities,
    defaultFiltersConfig,
    fullFiltersRange,
    isDesktopFiltersExpanded,
    setActiveFiltersConfig,
    setIsDesktopFiltersExpanded,
  } = useCatalog()

  const handleFilterChange = useCallback((id, value) => {
    setActiveFiltersConfig(prev => ({ ...prev, [id]: value }))
  }, [setActiveFiltersConfig])

  return (
    <div className={styles.wrapper}>
      {/* Main bar — row 1: sliders + CTA, row 2: non-slider main filters */}
      <CatalogMainBar
        activeFiltersCount={activeFiltersCount}
        catalogFilters={catalogCapabilities?.catalogFilters}
        defaultFiltersConfig={defaultFiltersConfig}
        filtersConfig={activeFiltersConfig}
        fullFiltersRange={fullFiltersRange}
        isExpanded={isDesktopFiltersExpanded}
        onFilterChange={handleFilterChange}
        onToggleExpand={() => setIsDesktopFiltersExpanded(prev => !prev)}
      />

      {/* Additional filters panel — slides in, unmounted when collapsed */}
      {isDesktopFiltersExpanded && (
        <CatalogAdditionalPanel
          catalogFilters={catalogCapabilities?.catalogFilters}
          defaultFiltersConfig={defaultFiltersConfig}
          filtersConfig={activeFiltersConfig}
          fullFiltersRange={fullFiltersRange}
          onFilterChange={handleFilterChange} 
        />
      )}

      {/* Circular expand/collapse button — sits on the bottom edge */}
      <Tooltip 
        title={isDesktopFiltersExpanded ? t('catalog.hide_extra_filters') : t('catalog.show_extra_filters')}
        placement='bottom'
      >
        <button
          type="button"
          className={styles.expandBtn}
          onClick={() => setIsDesktopFiltersExpanded(prev => !prev)}
          aria-expanded={isDesktopFiltersExpanded}
          aria-controls="catalog-additional-panel"
          aria-label={isDesktopFiltersExpanded ? t('catalog.hide_extra_filters') : t('catalog.show_extra_filters')}
          >
          <ChevronDown
            className={`${styles.expandChevron}${isDesktopFiltersExpanded ? ` ${styles.expandChevronOpen}` : ''}`}
            />
        </button>
      </Tooltip>
    </div>
  )
}
